import { useEffect, useState } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { GENIE_ANIMATION_MODE } from "@/constants/genieAnimation";
import { PATH_STOPS } from "@/lib/genieWaypoints";
import { MagicPathTransition } from "@/components/MagicPathTransition";

gsap.registerPlugin(ScrollTrigger);

const STOPS = PATH_STOPS;

export const MagicPath = () => {
    const [data, setData] = useState(null);
    const [strokeVisible, setStrokeVisible] = useState(true);
    const isMagicPathMode = GENIE_ANIMATION_MODE === "magic-path";

    useEffect(() => {
        if (isMagicPathMode) return undefined;
        let raf = null;
        let hideTrigger = null;

        const build = () => {
            if (window.innerWidth < 1024) {
                setData(null);
                return;
            }
            const vw = window.innerWidth;
            const pts = [];
            STOPS.forEach((stop) => {
                const node = document.querySelector(stop.id);
                if (!node) return;
                const top = node.getBoundingClientRect().top + window.scrollY;
                pts.push({ x: stop.xFrac * vw, y: top + node.offsetHeight * 0.5 });
            });
            if (pts.length < 2) return;
            let d = `M ${pts[0].x} ${Math.max(pts[0].y - 180, 110)}`;
            pts.forEach((p, i) => {
                if (i === 0) {
                    d += ` L ${p.x} ${p.y}`;
                    return;
                }
                const prev = pts[i - 1];
                if (i === pts.length - 1) {
                    d += ` Q ${(prev.x + p.x) / 2} ${(prev.y + p.y) / 2} ${p.x} ${p.y}`;
                } else {
                    const midY = (prev.y + p.y) / 2;
                    d += ` C ${prev.x} ${midY}, ${p.x} ${midY}, ${p.x} ${p.y}`;
                }
            });
            setData({ d, pts, height: document.documentElement.scrollHeight });
        };

        const schedule = () => {
            cancelAnimationFrame(raf);
            raf = requestAnimationFrame(build);
        };

        build();

        const cta = document.querySelector("#cta");
        if (cta) {
            hideTrigger = ScrollTrigger.create({
                trigger: cta,
                start: "bottom 72%",
                onEnter: () => setStrokeVisible(false),
                onLeaveBack: () => setStrokeVisible(true),
            });
        }

        window.addEventListener("load", schedule);
        window.addEventListener("resize", schedule);
        return () => {
            window.removeEventListener("load", schedule);
            window.removeEventListener("resize", schedule);
            cancelAnimationFrame(raf);
            hideTrigger?.kill();
        };
    }, [isMagicPathMode]);

    if (isMagicPathMode) {
        return <MagicPathTransition />;
    }

    if (!data) return null;

    return (
        <div
            className="pointer-events-none absolute inset-x-0 top-0 z-[5] hidden lg:block"
            style={{ height: data.height }}
            aria-hidden="true"
            data-testid="magic-path"
        >
            <svg width="100%" height={data.height} fill="none">
                <path
                    d={data.d}
                    stroke="#FDB927"
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeDasharray="0.5 18"
                    opacity={strokeVisible ? 0.4 : 0}
                />
                {data.pts.map((p, i) => (
                    <g key={`${p.x}-${p.y}`}>
                        <circle cx={p.x} cy={p.y} r="15" fill="#FDB927" opacity="0.1" />
                        <circle cx={p.x} cy={p.y} r="4.5" fill="#FDB927" opacity="0.5" />
                    </g>
                ))}
            </svg>
        </div>
    );
};
